import type { ChatRef } from "@kwp/contracts";
import { paths } from "@kwp/firestore";
import { Link } from "@tanstack/react-router";
import { doc, getDoc } from "firebase/firestore";
import { useEffect, useState, type ReactNode } from "react";
import { useClients } from "../../lib/clients";
import { safeHref } from "./markdown";

const chip =
  "inline-flex max-w-full items-center gap-1 rounded-full border border-border bg-surface px-2 py-0.5 text-xs font-medium text-text hover:bg-surface-2";

const KIND: Record<ChatRef["kind"], string> = { task: "Task", email: "Email", meeting: "Meeting" };

/**
 * A message's reference to a task, an email or a meeting. Tasks open their own page; emails
 * open the inbox (the thread view arrives with WP-007); meetings open their notes when the meeting
 * has any (WP-017), otherwise their Meet link.
 */
export function RefChip({ workspaceId, item }: { workspaceId: string; item: ChatRef }) {
  const content = (
    <>
      <span className="text-muted">{KIND[item.kind]}:</span>
      <span className="truncate">{item.label}</span>
    </>
  );
  if (item.kind === "task") {
    return (
      <Link to="/w/$workspaceId/tasks/$taskId" params={{ workspaceId, taskId: item.id }} className={chip} data-ref-kind="task">
        {content}
      </Link>
    );
  }
  if (item.kind === "email") {
    return (
      <Link to="/w/$workspaceId/inbox" params={{ workspaceId }} className={chip} title="Opens in the inbox" data-ref-kind="email">
        {content}
      </Link>
    );
  }
  return <MeetingChip workspaceId={workspaceId} meetingId={item.id}>{content}</MeetingChip>;
}

function MeetingChip({ workspaceId, meetingId, children }: { workspaceId: string; meetingId: string; children: ReactNode }) {
  const { firebase } = useClients();
  const [href, setHref] = useState<string | null>(null);
  const [withNotes, setWithNotes] = useState(false);

  useEffect(() => {
    let live = true;
    getDoc(doc(firebase.db, paths.meeting(workspaceId, meetingId)))
      .then((snap) => {
        const uri: unknown = snap.get("meetUri");
        const status: unknown = snap.get("notes.status");
        if (!live) return;
        if (typeof uri === "string") setHref(safeHref(uri));
        setWithNotes(typeof status === "string" && status !== "none");
      })
      .catch((e: unknown) => console.error(e));
    return () => {
      live = false;
    };
  }, [firebase.db, workspaceId, meetingId]);

  if (withNotes) {
    return (
      <Link to="/w/$workspaceId/meetings/$meetingId" params={{ workspaceId, meetingId }} className={chip} title="Meeting notes" data-ref-kind="meeting">
        {children}
      </Link>
    );
  }
  if (href === null) return <span className={chip} data-ref-kind="meeting">{children}</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={chip} data-ref-kind="meeting">
      {children}
    </a>
  );
}
